/**
 * Elite Marketing Workflow Templates (9–15) — Production-grade prebuilt workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

export const ELITE_MARKETING_WORKFLOWS: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 9. AI CONTENT WRITER (10 nodes)
  // ===================================================================

  "AI Content Writer": {
    nodes: [
      trigger("Manual: Submit content brief", "manual", {
        howItStarts:
          "You provide a content topic, target keyword, and audience",
        setup:
          "Type your brief in chat: topic, primary keyword, target audience, and tone. Or POST to your workflow API endpoint.",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "How to reduce SaaS churn with onboarding automation",
          },
          {
            name: "primary_keyword",
            type: "string",
            example: "SaaS onboarding automation",
          },
          {
            name: "target_audience",
            type: "string",
            example: "B2B SaaS founders and product managers",
          },
          {
            name: "tone",
            type: "string",
            example: "authoritative but approachable",
          },
          { name: "editor_email", type: "string" },
        ],
      }),
      // 2 — SERP research via SerpAPI
      n("2", "hitApi", "SERP research via SerpAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={primary_keyword}&num=10&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_results",
              type: "number",
            },
            {
              name: "related_searches",
              customName: "related_searches",
              type: "string",
            },
          ],
        },
      }),
      // 3 — AI outline
      aiNode(
        "3",
        "AI generate content outline",
        520,
        "You are an SEO content strategist. Analyze the SERP results and create a detailed blog outline that will outperform existing content. Include H2s, H3s, and key points per section. Respond JSON: {title: string, outline_html: string, word_count_target: number, unique_angle: string, sections_count: number}",
        "Topic: {topic}\\nKeyword: {primary_keyword}\\nAudience: {target_audience}\\nTone: {tone}\\nTop SERP results: {serp_results}\\nRelated searches: {related_searches}",
        [
          { field: "title", as: "article_title", type: "string" },
          { field: "outline_html", as: "outline_html", type: "string" },
          {
            field: "word_count_target",
            as: "word_count_target",
            type: "number",
          },
          { field: "unique_angle", as: "unique_angle", type: "string" },
          { field: "sections_count", as: "sections_count", type: "number" },
        ],
      ),
      // 4 — AI draft with source grounding
      aiNode(
        "4",
        "AI write full draft with sources",
        760,
        "You are an expert content writer. Write the full article based on the outline. Ground claims with references to the SERP sources. Use proper HTML formatting with h2, h3, p, ul, li tags. Include a conclusion with CTA. Respond JSON: {body_html: string, word_count: number, sources_cited: number}",
        "Title: {article_title}\\nOutline: {outline_html}\\nTarget words: {word_count_target}\\nAngle: {unique_angle}\\nSERP sources for grounding: {serp_results}\\nTone: {tone}",
        [
          { field: "body_html", as: "body_html", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "sources_cited", as: "sources_cited", type: "number" },
        ],
      ),
      // 5 — AI meta generation
      aiNode(
        "5",
        "AI generate SEO meta tags",
        1000,
        "Generate SEO metadata for this article. Respond JSON: {meta_title: string, meta_description: string, slug: string, focus_keyphrase: string, og_description: string}",
        "Title: {article_title}\\nKeyword: {primary_keyword}\\nWord count: {word_count}",
        [
          { field: "meta_title", as: "meta_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "slug", as: "slug", type: "string" },
          { field: "focus_keyphrase", as: "focus_keyphrase", type: "string" },
          { field: "og_description", as: "og_description", type: "string" },
        ],
        460,
      ),
      // 6 — AI social variants
      aiNode(
        "6",
        "AI create social media variants",
        1240,
        "Create social media posts promoting this article. Twitter (280 chars + hashtags), LinkedIn (professional, 1200 chars), and a newsletter teaser (2-3 sentences). Respond JSON: {twitter_post: string, linkedin_post: string, newsletter_teaser: string}",
        "Title: {article_title}\\nMeta: {meta_description}\\nKeyword: {primary_keyword}",
        [
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "newsletter_teaser",
            as: "newsletter_teaser",
            type: "string",
          },
        ],
      ),
      // 7 — Editor approval gate
      n("7", "approvalGate", "Editor reviews draft + meta", 1480, {
        approvalGateData: {
          message:
            "Review article: {article_title}\\n\\nWord count: {word_count} | Sources cited: {sources_cited}\\nMeta title: {meta_title}\\nSlug: /{slug}",
        },
      }),
      // 8 — CMS publish via WordPress API
      n("8", "hitApi", "Publish to WordPress", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://YOUR_SITE.com/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wordpress_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{article_title}" },
            { key: "content", value: "{body_html}" },
            { key: "slug", value: "{slug}" },
            { key: "status", value: "publish" },
            {
              key: "meta",
              value:
                '{"_yoast_wpseo_metadesc":"{meta_description}","_yoast_wpseo_focuskw":"{focus_keyphrase}"}',
            },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "number" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),
      // 9 — Social schedule via Buffer
      n(
        "9",
        "hitApi",
        "Schedule social posts via Buffer",
        1960,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.bufferapp.com/1/updates/create.json",
            headers: [
              { key: "Authorization", value: "Bearer {buffer_token}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [
              { key: "text", value: "{twitter_post} {wp_post_url}" },
              { key: "profile_ids[]", value: "{buffer_profile_id}" },
              { key: "scheduled_at", value: "now" },
            ],
            variables: [
              {
                name: "success",
                customName: "buffer_success",
                type: "boolean",
              },
              {
                name: "updates[0].id",
                customName: "buffer_update_id",
                type: "string",
              },
            ],
          },
        },
        460,
      ),
      // 10 — Performance tagging
      slackNode(
        "10",
        "Log publication to #content",
        2200,
        "Published: {article_title}\\nURL: {wp_post_url}\\nWord count: {word_count} | Sources: {sources_cited}\\nSocial scheduled via Buffer\\nKeyword: {primary_keyword}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "10"),
    ],
    summary: `**AI Content Writer** — Full SERP-grounded content pipeline (10 steps)\n\n1. Submit a content brief (topic, keyword, audience, tone)\n2. **SerpAPI** researches the top 10 results for your keyword\n3. AI creates a strategic outline to outperform competitors\n4. AI writes the full draft grounded in real SERP sources\n5. AI generates SEO meta tags (title, description, slug, OG)\n6. AI creates Twitter, LinkedIn, and newsletter variants\n7. **Editor approval gate** — nothing publishes without review\n8. Publishes to **WordPress** via REST API\n9. Schedules social posts via **Buffer**\n10. Logs the publication to Slack\n\n**To activate:** Say "connect openai", "connect serpapi", "connect wordpress", "connect buffer", "connect slack"`,
  },

  // ===================================================================
  // 10. CONTENT REPURPOSER (8 nodes)
  // ===================================================================

  "Content Repurposer": {
    nodes: [
      trigger("Webhook: Published content notification", "webhook_app", {
        source: "WordPress / CMS",
        event: "post.published",
        howItStarts: "Your CMS fires a webhook when new content is published",
        setup:
          "In WordPress → Settings → Webhooks (or WP Webhooks plugin) → Add webhook for 'Post Published' → paste your workflow URL. The AI will read whatever data your CMS sends.",
        inputFields: [],
      }),
      // 2 — Extract canonical content
      aiNode(
        "2",
        "AI extract canonical content",
        280,
        "Read this CMS webhook payload and extract the published content. Different CMS platforms send data in different formats — find the article title, full body text, author name, and published URL regardless of field naming. Strip HTML to plain text for the body. Respond JSON: {article_title: string, article_body: string, author_name: string, article_url: string, word_count: number}",
        "{webhook_payload}",
        [
          { field: "article_title", as: "article_title", type: "string" },
          { field: "article_body", as: "article_body", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "article_url", as: "article_url", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),
      // 3 — AI message map (top takeaways)
      aiNode(
        "3",
        "AI extract key message map",
        520,
        "Analyze this article and extract the top 5 takeaways, the core thesis, and the primary audience. These will be used to create derivative content. Respond JSON: {core_thesis: string, takeaways: string, primary_audience: string, content_type: string, emotional_hook: string}",
        "Title: {article_title}\\nBody: {article_body}",
        [
          { field: "core_thesis", as: "core_thesis", type: "string" },
          { field: "takeaways", as: "takeaways", type: "string" },
          { field: "primary_audience", as: "primary_audience", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "emotional_hook", as: "emotional_hook", type: "string" },
        ],
      ),
      // 4 — Channel-specific variants
      aiNode(
        "4",
        "AI generate channel variants",
        760,
        "Repurpose this content into 3 channel-specific formats. Twitter thread (5-7 tweets, each under 280 chars, numbered), LinkedIn post (professional, 1200 chars, with line breaks), and Newsletter section (3-4 paragraphs with a personal angle). Respond JSON: {twitter_thread: string, linkedin_post: string, newsletter_section: string}",
        "Title: {article_title}\\nThesis: {core_thesis}\\nTakeaways: {takeaways}\\nAudience: {primary_audience}\\nHook: {emotional_hook}\\nURL: {article_url}",
        [
          { field: "twitter_thread", as: "twitter_thread", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "newsletter_section",
            as: "newsletter_section",
            type: "string",
          },
        ],
      ),
      // 5 — Creative brief
      aiNode(
        "5",
        "AI generate creative brief",
        1000,
        "Create a visual creative brief for the design team to make graphics for social posts promoting this article. Include suggested image concepts, text overlays, color mood, and dimensions needed. Respond JSON: {image_concept: string, text_overlay: string, color_mood: string, dimensions: string, design_notes: string}",
        "Title: {article_title}\\nThesis: {core_thesis}\\nHook: {emotional_hook}\\nAudience: {primary_audience}",
        [
          { field: "image_concept", as: "image_concept", type: "string" },
          { field: "text_overlay", as: "text_overlay", type: "string" },
          { field: "color_mood", as: "color_mood", type: "string" },
          { field: "dimensions", as: "dimensions", type: "string" },
          { field: "design_notes", as: "design_notes", type: "string" },
        ],
        460,
      ),
      // 6 — Editorial approval gate
      n("6", "approvalGate", "Editorial review of all variants", 1240, {
        approvalGateData: {
          message:
            "Review repurposed content for: {article_title}\\n\\nTwitter thread, LinkedIn post, newsletter section, and creative brief are ready for review.",
        },
      }),
      // 7 — Schedule/publish via Buffer
      n("7", "hitApi", "Schedule posts via Buffer", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "text", value: "{linkedin_post}" },
            { key: "profile_ids[]", value: "{buffer_profile_id}" },
            { key: "scheduled_at", value: "now" },
          ],
          variables: [
            {
              name: "success",
              customName: "buffer_scheduled",
              type: "boolean",
            },
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
          ],
        },
      }),
      // 8 — Performance feedback
      slackNode(
        "8",
        "Log repurpose to #content-ops",
        1720,
        "Repurposed: {article_title}\\nSource: {article_url}\\nVariants: Twitter thread, LinkedIn, Newsletter\\nCreative brief sent to design\\nScheduled via Buffer",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Content Repurposer** — Turn one article into multi-channel content (8 steps)\n\n1. CMS fires a webhook when content is published\n2. **AI extracts** the canonical content (title, body, URL)\n3. AI creates a **message map** — core thesis, takeaways, audience\n4. AI generates **Twitter thread, LinkedIn post, and newsletter section**\n5. AI creates a **creative brief** for the design team\n6. **Editorial approval gate** — review all variants before publishing\n7. Schedules posts via **Buffer**\n8. Logs the repurpose event to Slack\n\n**To activate:** Say "connect openai", "connect buffer", "connect slack"`,
  },

  // ===================================================================
  // 11. EMAIL NURTURE SEQUENCE (9 nodes)
  // ===================================================================

  "Email Nurture Sequence (Advanced)": {
    nodes: [
      trigger("Webhook: New subscriber with consent", "webhook_form", {
        source: "Signup Form / Landing Page",
        howItStarts:
          "Someone subscribes via your form and grants marketing consent",
        setup:
          "Connect your signup form → Webhooks → paste your workflow URL. The AI will read whatever data your form sends and extract what it needs. Ensure your form collects marketing consent.",
        inputFields: [],
      }),
      // 2 — AI segment the audience
      aiNode(
        "2",
        "AI segment subscriber",
        280,
        "Read this form/signup webhook data and extract the subscriber details. Then segment them into one of these buckets: founder, marketer, developer, or general. Respond JSON: {subscriber_email: string, subscriber_name: string, company_name: string, role: string, segment: string, has_consent: string}",
        "{webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "role", as: "role", type: "string" },
          { field: "segment", as: "segment", type: "string" },
          { field: "has_consent", as: "has_consent", type: "string" },
        ],
      ),
      // 3 — Welcome email
      emailNode(
        "3",
        "Send personalized welcome email",
        520,
        "{subscriber_email}",
        "Welcome to the community, {subscriber_name}!",
        "<p>Hi {subscriber_name},</p><p>Thanks for joining! As a {role} at {company_name}, here's what we've curated for you:</p><p>We've placed you in our <strong>{segment}</strong> track, which means you'll get content specifically relevant to your work.</p><p>Reply to this email anytime — a real human reads every response.</p>",
      ),
      // 4 — Wait 2 days
      n("4", "delayAction", "Wait 2 days", 760, {
        delayAction: { time: 2, timeFormat: "days" },
      }),
      // 5 — Educational content email
      emailNode(
        "5",
        "Send educational content",
        1000,
        "{subscriber_email}",
        "3 strategies for {segment}s like you",
        "<p>Hi {subscriber_name},</p><p>Based on your {segment} profile, here are three strategies other {role}s at companies like {company_name} are using right now:</p><p><strong>1.</strong> Automate your most repetitive workflow first<br/><strong>2.</strong> Set up alerts for the metrics that matter most<br/><strong>3.</strong> Use AI to draft, then edit with your expertise</p><p>Which resonates most? Hit reply and let us know.</p>",
      ),
      // 6 — Behavior scoring condition
      n("6", "condition", "High engagement? (segment = founder)", 1240, {
        conditionData: [
          {
            variable: "segment",
            value: "founder",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      // 7 — Intent handoff to sales (Slack) — True branch
      slackNode(
        "7",
        "Handoff to sales (high intent)",
        1480,
        "Hot lead from nurture sequence:\\nName: {subscriber_name}\\nEmail: {subscriber_email}\\nCompany: {company_name}\\nRole: {role}\\nSegment: {segment}\\nCompleted welcome + education emails — ready for outreach",
      ),
      // 8 — Re-engage or pause condition — False branch
      n(
        "8",
        "condition",
        "Has marketing consent?",
        1480,
        {
          conditionData: [
            {
              variable: "has_consent",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
      // 9 — Exit + suppression email
      emailNode(
        "9",
        "Send re-engagement email",
        1720,
        "{subscriber_email}",
        "Still interested, {subscriber_name}?",
        "<p>Hi {subscriber_name},</p><p>We noticed you might prefer less email. No worries — here's a quick recap of what you've missed, and a link to manage your preferences.</p><p>If we don't hear from you, we'll pause your emails automatically. No hard feelings!</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("8", "9", "output_1"),
    ],
    summary: `**Email Nurture Sequence (Advanced)** — Segmented drip with sales handoff (9 steps)\n\n1. Subscriber signs up with marketing consent\n2. **AI segments** into founder / marketer / developer / general\n3. Personalized welcome email (mentions role, company, segment)\n4. Wait 2 days\n5. Educational content email tailored to segment\n6. **Behavior check:** Founders → handed off to sales via Slack\n7. Others → consent check → re-engagement email or suppression\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 12. SOCIAL MEDIA SCHEDULER (9 nodes)
  // ===================================================================

  "Social Media Scheduler (Pro)": {
    nodes: [
      trigger(
        "Webhook: Content calendar row from Google Sheets",
        "webhook_app",
        {
          source: "Google Sheets",
          event: "row.created",
          howItStarts:
            "A new row is added to your content calendar spreadsheet",
          setup:
            "Use Google Apps Script or Zapier to POST to your workflow URL when a new row is added to your content calendar sheet. The AI will read whatever data the sheet sends.",
          inputFields: [],
        },
      ),
      // 2 — AI copy enhancement
      aiNode(
        "2",
        "AI enhance copy for each platform",
        280,
        "Read this Google Sheets webhook payload and extract the content calendar entry. Then enhance the copy for Twitter (280 chars, punchy), LinkedIn (professional, 1200 chars), and Instagram (casual, emoji-friendly). Respond JSON: {post_topic: string, raw_copy: string, scheduled_date: string, twitter_copy: string, linkedin_copy: string, instagram_copy: string, hashtags: string, content_pillar: string}",
        "{webhook_payload}",
        [
          { field: "post_topic", as: "post_topic", type: "string" },
          { field: "raw_copy", as: "raw_copy", type: "string" },
          { field: "scheduled_date", as: "scheduled_date", type: "string" },
          { field: "twitter_copy", as: "twitter_copy", type: "string" },
          { field: "linkedin_copy", as: "linkedin_copy", type: "string" },
          { field: "instagram_copy", as: "instagram_copy", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "content_pillar", as: "content_pillar", type: "string" },
        ],
      ),
      // 3 — Media resolution (check for attached media URL)
      n("3", "hitApi", "Resolve media assets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.unsplash.com/search/photos?query={post_topic}&per_page=1&client_id={unsplash_access_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "results[0].urls.regular",
              customName: "media_url",
              type: "string",
            },
            {
              name: "results[0].alt_description",
              customName: "media_alt",
              type: "string",
            },
            {
              name: "results[0].user.name",
              customName: "media_credit",
              type: "string",
            },
          ],
        },
      }),
      // 4 — Preview assembly
      aiNode(
        "4",
        "AI assemble preview package",
        760,
        "Assemble a preview of all social posts for review. Format them cleanly with platform labels. Respond JSON: {preview_html: string, post_count: number, estimated_reach: string}",
        "Topic: {post_topic}\\nTwitter: {twitter_copy}\\nLinkedIn: {linkedin_copy}\\nInstagram: {instagram_copy}\\nHashtags: {hashtags}\\nMedia: {media_url} (credit: {media_credit})\\nScheduled: {scheduled_date}",
        [
          { field: "preview_html", as: "preview_html", type: "string" },
          { field: "post_count", as: "post_count", type: "number" },
          { field: "estimated_reach", as: "estimated_reach", type: "string" },
        ],
      ),
      // 5 — Approval gate
      n("5", "approvalGate", "Approve social posts before scheduling", 1000, {
        approvalGateData: {
          message:
            "Review {post_count} social posts for: {post_topic}\\nScheduled: {scheduled_date}\\nEst. reach: {estimated_reach}",
        },
      }),
      // 6 — Schedule execution via Buffer
      n("6", "hitApi", "Schedule via Buffer API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "text", value: "{linkedin_copy} {hashtags}" },
            { key: "media[link]", value: "{media_url}" },
            { key: "profile_ids[]", value: "{buffer_profile_id}" },
            { key: "scheduled_at", value: "{scheduled_date}" },
          ],
          variables: [
            { name: "success", customName: "buffer_success", type: "boolean" },
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
            {
              name: "updates[0].due_at",
              customName: "scheduled_time",
              type: "string",
            },
          ],
        },
      }),
      // 7 — Post verification
      n("7", "condition", "Buffer scheduled successfully?", 1480, {
        conditionData: [
          {
            variable: "buffer_success",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      // 8 — Calendar writeback via Google Sheets API
      n("8", "hitApi", "Update calendar sheet (status = scheduled)", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{sheet_name}!F{row_number}?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["scheduled","' +
                "{buffer_update_id}" +
                '","' +
                "{scheduled_time}" +
                '"]]',
            },
          ],
          variables: [
            {
              name: "updatedCells",
              customName: "cells_updated",
              type: "number",
            },
          ],
        },
      }),
      // 9 — Performance capture
      slackNode(
        "9",
        "Log to #social-ops",
        1960,
        "Scheduled: {post_topic}\\nPlatforms: Twitter, LinkedIn, Instagram\\nDate: {scheduled_date}\\nBuffer ID: {buffer_update_id}\\nMedia: {media_url} (credit: {media_credit})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("8", "9"),
    ],
    summary: `**Social Media Scheduler (Pro)** — Calendar-driven social publishing (9 steps)\n\n1. New row added to your Google Sheets content calendar\n2. **AI enhances** copy for Twitter, LinkedIn, and Instagram\n3. **Unsplash** resolves a stock photo for the topic\n4. AI assembles a preview package for review\n5. **Approval gate** — nothing posts without sign-off\n6. Schedules via **Buffer API** with media attachment\n7. Verifies scheduling succeeded\n8. Writes status back to your **Google Sheet**\n9. Logs to Slack for team visibility\n\n**To activate:** Say "connect openai", "connect unsplash", "connect buffer", "connect google-sheets", "connect slack"`,
  },

  // ===================================================================
  // 13. NEWSLETTER CURATOR (8 nodes)
  // ===================================================================

  "Newsletter Curator": {
    nodes: [
      trigger("Schedule: Every Monday at 7:00 AM", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Weekly automated newsletter curation — pulls fresh content from RSS feeds every Monday morning",
        setup:
          "Activate this workflow. Configure your RSS feed URLs in the first API node. Say 'connect openai', 'connect email', and 'connect slack'.",
        inputFields: [
          {
            name: "newsletter_name",
            type: "string",
            example: "The Weekly Briefing",
          },
          {
            name: "rss_feed_urls",
            type: "string",
            example:
              "https://techcrunch.com/feed,https://blog.hubspot.com/rss.xml",
          },
          { name: "editor_email", type: "string" },
          { name: "subscriber_list_id", type: "string" },
        ],
      }),
      // 2 — RSS pull via rss2json
      n("2", "hitApi", "Pull latest RSS articles", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.rss2json.com/v1/api.json?rss_url={rss_feed_urls}&api_key={rss2json_key}&count=20",
          headers: [],
          body: [],
          variables: [
            { name: "items", customName: "rss_items", type: "string" },
            { name: "feed.title", customName: "feed_title", type: "string" },
            { name: "status", customName: "rss_status", type: "string" },
          ],
        },
      }),
      // 3 — AI relevance scoring + dedupe
      aiNode(
        "3",
        "AI score relevance and deduplicate",
        520,
        "You are a newsletter editor. Score each article 1-10 for relevance and newsworthiness. Remove duplicates (same story from different sources). Keep only the top 5 articles. Respond JSON: {curated_articles: string, article_count: number, topics_covered: string, dropped_count: number}",
        "Newsletter: {newsletter_name}\\nRSS items: {rss_items}",
        [
          { field: "curated_articles", as: "curated_articles", type: "string" },
          { field: "article_count", as: "article_count", type: "number" },
          { field: "topics_covered", as: "topics_covered", type: "string" },
          { field: "dropped_count", as: "dropped_count", type: "number" },
        ],
      ),
      // 4 — AI intro + framing
      aiNode(
        "4",
        "AI write newsletter intro + framing",
        760,
        "Write a newsletter introduction (3-4 sentences) and a brief framing paragraph for each curated article. Be conversational, opinionated, and add context the reader won't get from headlines alone. Respond JSON: {intro_paragraph: string, article_frames: string, sign_off: string, subject_line: string}",
        "Newsletter: {newsletter_name}\\nCurated articles: {curated_articles}\\nTopics: {topics_covered}",
        [
          { field: "intro_paragraph", as: "intro_paragraph", type: "string" },
          { field: "article_frames", as: "article_frames", type: "string" },
          { field: "sign_off", as: "sign_off", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
        ],
      ),
      // 5 — HTML assembly (code node)
      n("5", "runCode", "Assemble newsletter HTML", 1000, {
        runCodeDetails: {
          code: `// Assemble the final newsletter HTML from curated content
const intro = inputs.intro_paragraph || "";
const frames = inputs.article_frames || "";
const signOff = inputs.sign_off || "";
const name = inputs.newsletter_name || "Newsletter";
const subject = inputs.subject_line || "This Week's Roundup";

const html = \`
<div style="max-width:600px;margin:0 auto;font-family:Georgia,serif;">
  <h1 style="border-bottom:3px solid #333;padding-bottom:12px;">\${name}</h1>
  <p style="font-size:18px;line-height:1.6;color:#333;">\${intro}</p>
  <hr style="border:1px solid #eee;"/>
  <div style="font-size:16px;line-height:1.7;color:#444;">\${frames}</div>
  <hr style="border:1px solid #eee;"/>
  <p style="font-style:italic;color:#666;">\${signOff}</p>
</div>
\`;

return { newsletter_html: html, final_subject: subject, assembled: true };`,
          language: "javascript",
          outputVariables: [
            { name: "newsletter_html", type: "string" },
            { name: "final_subject", type: "string" },
            { name: "assembled", type: "boolean" },
          ],
        },
      }),
      // 6 — Editor approval
      n("6", "approvalGate", "Editor reviews newsletter", 1240, {
        approvalGateData: {
          message:
            "Review this week's newsletter:\\nSubject: {final_subject}\\nArticles: {article_count} curated from {feed_title}\\nTopics: {topics_covered}",
        },
      }),
      // 7 — Send via ESP (Postmark)
      n("7", "hitApi", "Send newsletter via Postmark", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.postmarkapp.com/email",
          headers: [
            { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "From", value: "{sender_email}" },
            { key: "To", value: "{subscriber_list_id}" },
            { key: "Subject", value: "{final_subject}" },
            { key: "HtmlBody", value: "{newsletter_html}" },
            { key: "MessageStream", value: "broadcast" },
          ],
          variables: [
            {
              name: "MessageID",
              customName: "postmark_message_id",
              type: "string",
            },
            {
              name: "ErrorCode",
              customName: "postmark_error_code",
              type: "number",
            },
          ],
        },
      }),
      // 8 — Metrics + archive
      slackNode(
        "8",
        "Archive + metrics to #newsletter",
        1720,
        "Newsletter sent: {final_subject}\\nArticles curated: {article_count}\\nDropped (dupes/low relevance): {dropped_count}\\nPostmark ID: {postmark_message_id}\\nTopics: {topics_covered}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Newsletter Curator** — Automated weekly newsletter pipeline (8 steps)\n\n1. Runs every Monday at 7 AM\n2. Pulls latest articles from your **RSS feeds**\n3. **AI scores relevance** (1-10) and deduplicates — keeps top 5\n4. AI writes a conversational **intro + framing** for each article\n5. **Code node** assembles the final HTML newsletter\n6. **Editor approval gate** — review before sending\n7. Sends via **Postmark** broadcast stream\n8. Archives metrics to Slack\n\n**To activate:** Say "connect openai", "connect postmark", "connect slack"`,
  },

  // ===================================================================
  // 14. BRAND MENTION MONITOR (8 nodes)
  // ===================================================================

  "Brand Mention Monitor (Advanced)": {
    nodes: [
      trigger("Schedule: Every 4 hours", "schedule", {
        schedule: "Every 4 hours",
        howItStarts:
          "Scans social media and news for brand mentions every 4 hours",
        setup:
          "Activate this workflow. Configure your brand name and monitoring keywords. Say 'connect openai' and 'connect slack'.",
        inputFields: [
          { name: "brand_name", type: "string", example: "OmniEngage" },
          {
            name: "monitoring_keywords",
            type: "string",
            example: "OmniEngage,omni engage,@omniengage",
          },
          { name: "crisis_email", type: "string", example: "pr@company.com" },
        ],
      }),
      // 2 — Social scan via SerpAPI (news + social)
      n("2", "hitApi", "Scan mentions via SerpAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={monitoring_keywords}&tbm=nws&tbs=qdr:h4&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "raw_mentions",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_mentions",
              type: "number",
            },
          ],
        },
      }),
      // 3 — Entity resolution (code node)
      n("3", "runCode", "Entity resolution and deduplication", 520, {
        runCodeDetails: {
          code: `// Deduplicate mentions, normalize entity names, filter out irrelevant matches
const mentions = inputs.raw_mentions || "[]";
const brand = (inputs.brand_name || "").toLowerCase();
let items;
try { items = typeof mentions === "string" ? JSON.parse(mentions) : mentions; } catch(e) { items = []; }
if (!Array.isArray(items)) items = [];

const seen = new Set();
const unique = items.filter(item => {
  const title = (item.title || "").toLowerCase();
  const link = item.link || "";
  if (seen.has(link)) return false;
  seen.add(link);
  return title.includes(brand) || (item.snippet || "").toLowerCase().includes(brand);
});

return {
  filtered_mentions: JSON.stringify(unique),
  mention_count: unique.length,
  has_mentions: unique.length > 0 ? "true" : "false"
};`,
          language: "javascript",
          outputVariables: [
            { name: "filtered_mentions", type: "string" },
            { name: "mention_count", type: "number" },
            { name: "has_mentions", type: "string" },
          ],
        },
      }),
      // 4 — Mention normalization + AI sentiment + urgency
      aiNode(
        "4",
        "AI analyze sentiment and urgency",
        760,
        "Analyze these brand mentions for sentiment and urgency. For each mention, classify sentiment (positive/neutral/negative) and urgency (low/medium/high). Then provide an overall summary. A mention is urgent if it's negative AND from a high-authority source or going viral. Respond JSON: {overall_sentiment: string, positive_count: number, negative_count: number, neutral_count: number, urgent_count: number, is_crisis: string, mention_summary: string, top_mention_title: string, top_mention_source: string}",
        "Brand: {brand_name}\\nMentions: {filtered_mentions}",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "neutral_count", as: "neutral_count", type: "number" },
          { field: "urgent_count", as: "urgent_count", type: "number" },
          { field: "is_crisis", as: "is_crisis", type: "string" },
          { field: "mention_summary", as: "mention_summary", type: "string" },
          {
            field: "top_mention_title",
            as: "top_mention_title",
            type: "string",
          },
          {
            field: "top_mention_source",
            as: "top_mention_source",
            type: "string",
          },
        ],
      ),
      // 5 — Crisis branch (condition: negative + urgent)
      n("5", "condition", "Crisis detected? (negative + urgent)", 1000, {
        conditionData: [
          {
            variable: "is_crisis",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      // 6 — Crisis alert (True branch)
      emailNode(
        "6",
        "Crisis alert to PR team",
        1240,
        "{crisis_email}",
        "URGENT: Brand crisis detected — {brand_name}",
        "<h2 style='color:red;'>Brand Crisis Alert</h2><p><strong>Sentiment:</strong> {overall_sentiment}</p><p><strong>Negative mentions:</strong> {negative_count} | <strong>Urgent:</strong> {urgent_count}</p><p><strong>Top mention:</strong> {top_mention_title} ({top_mention_source})</p><p><strong>Summary:</strong> {mention_summary}</p><p>Immediate review and response recommended.</p>",
      ),
      // 7 — Opportunity branch + digest logging (False branch)
      slackNode(
        "7",
        "Post digest to #brand-monitoring",
        1240,
        "Brand Monitor ({brand_name}) — {mention_count} mentions\\nSentiment: {overall_sentiment}\\n+{positive_count} / ~{neutral_count} / -{negative_count}\\nTop: {top_mention_title} ({top_mention_source})\\n{mention_summary}",
        460,
      ),
      // 8 — Response tracking
      slackNode(
        "8",
        "Log to #brand-response-tracking",
        1480,
        "Scan complete: {mention_count} mentions processed\\nCrisis: {is_crisis} | Urgent: {urgent_count}\\nSentiment breakdown: +{positive_count} ~{neutral_count} -{negative_count}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**Brand Mention Monitor (Advanced)** — Real-time brand protection (8 steps)\n\n1. Scans **SerpAPI news** every 4 hours for brand mentions\n2. **Code node** deduplicates and filters irrelevant matches\n3. **AI analyzes sentiment** (positive/neutral/negative) and urgency\n4. **Crisis branch:** negative + urgent → emails PR team immediately\n5. **Normal branch:** posts digest to Slack #brand-monitoring\n6. All results logged to #brand-response-tracking\n\n**To activate:** Say "connect openai", "connect serpapi", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 15. SEO KEYWORD MONITOR (8 nodes)
  // ===================================================================

  "SEO Keyword Monitor": {
    nodes: [
      trigger("Schedule: Every Monday at 6:00 AM", "schedule", {
        schedule: "Every Monday at 6:00 AM",
        howItStarts:
          "Weekly automated SEO rank tracking — checks keyword positions every Monday",
        setup:
          "Activate this workflow. Configure your target keywords and domain in the API node. Say 'connect openai', 'connect email', and 'connect slack'.",
        inputFields: [
          { name: "target_domain", type: "string", example: "omniengage.com" },
          {
            name: "keywords",
            type: "string",
            example:
              "workflow automation,email marketing automation,saas onboarding",
          },
          {
            name: "seo_team_email",
            type: "string",
            example: "seo@company.com",
          },
          { name: "spreadsheet_id", type: "string" },
        ],
      }),
      // 2 — Rank check via SerpAPI
      n("2", "hitApi", "Check keyword rankings via SerpAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={keywords}&num=100&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_results",
              type: "number",
            },
            {
              name: "search_parameters.q",
              customName: "searched_keyword",
              type: "string",
            },
          ],
        },
      }),
      // 3 — Delta computation (code node)
      n("3", "runCode", "Compute rank deltas", 520, {
        runCodeDetails: {
          code: `// Parse SERP results and compute current rankings for our domain
const results = inputs.serp_results || "[]";
const domain = (inputs.target_domain || "").toLowerCase();
let items;
try { items = typeof results === "string" ? JSON.parse(results) : results; } catch(e) { items = []; }
if (!Array.isArray(items)) items = [];

const domainResults = items
  .map((item, idx) => ({ position: idx + 1, title: item.title, link: item.link }))
  .filter(item => (item.link || "").toLowerCase().includes(domain));

const bestPosition = domainResults.length > 0 ? domainResults[0].position : -1;
const isRanking = domainResults.length > 0;
const positionsFound = domainResults.length;
const keyword = inputs.searched_keyword || "unknown";

// Compare with previous (stored as input — in production, read from sheet)
const previousPosition = parseInt(inputs.previous_position || "0", 10);
const delta = previousPosition > 0 && bestPosition > 0 ? previousPosition - bestPosition : 0;
const direction = delta > 0 ? "improved" : delta < 0 ? "dropped" : "unchanged";
const hasDrop = delta < -2 ? "true" : "false";

return {
  current_position: bestPosition,
  positions_found: positionsFound,
  is_ranking: isRanking ? "true" : "false",
  delta: delta,
  direction: direction,
  has_significant_drop: hasDrop,
  rank_summary: keyword + ": #" + bestPosition + " (" + direction + " by " + Math.abs(delta) + ")"
};`,
          language: "javascript",
          outputVariables: [
            { name: "current_position", type: "number" },
            { name: "positions_found", type: "number" },
            { name: "is_ranking", type: "string" },
            { name: "delta", type: "number" },
            { name: "direction", type: "string" },
            { name: "has_significant_drop", type: "string" },
            { name: "rank_summary", type: "string" },
          ],
        },
      }),
      // 4 — Priority classification (condition: significant drop?)
      n("4", "condition", "Significant rank drop? (>2 positions)", 760, {
        conditionData: [
          {
            variable: "has_significant_drop",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      // 5 — AI diagnosis of drops (True branch)
      aiNode(
        "5",
        "AI diagnose ranking drop",
        1000,
        "A keyword ranking dropped significantly. Analyze the SERP landscape and suggest possible causes and remediation steps. Consider algorithm updates, competitor moves, technical SEO issues, and content freshness. Respond JSON: {likely_cause: string, competitor_threat: string, remediation_steps: string, urgency: string, estimated_recovery_days: number}",
        "Keyword: {searched_keyword}\\nCurrent position: {current_position}\\nDelta: {delta} positions\\nDirection: {direction}\\nSERP landscape: {serp_results}\\nOur domain: {target_domain}",
        [
          { field: "likely_cause", as: "likely_cause", type: "string" },
          {
            field: "competitor_threat",
            as: "competitor_threat",
            type: "string",
          },
          {
            field: "remediation_steps",
            as: "remediation_steps",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "estimated_recovery_days",
            as: "estimated_recovery_days",
            type: "number",
          },
        ],
      ),
      // 6 — Task generation (Slack alert with action items)
      slackNode(
        "6",
        "Alert SEO team with action items",
        1240,
        "SEO Alert: {searched_keyword} dropped {delta} positions to #{current_position}\\n\\nLikely cause: {likely_cause}\\nCompetitor threat: {competitor_threat}\\nUrgency: {urgency}\\n\\nRemediation:\\n{remediation_steps}\\n\\nEst. recovery: {estimated_recovery_days} days",
      ),
      // 7 — Report email (both branches converge here)
      emailNode(
        "7",
        "Weekly SEO rank report",
        1480,
        "{seo_team_email}",
        "SEO Rank Report: {searched_keyword} — {direction}",
        "<h2>Weekly SEO Keyword Report</h2><p><strong>Keyword:</strong> {searched_keyword}</p><p><strong>Current position:</strong> #{current_position}</p><p><strong>Change:</strong> {direction} by {delta} positions</p><p><strong>Domain:</strong> {target_domain}</p><p><strong>Pages found in top 100:</strong> {positions_found}</p>",
      ),
      // 8 — History update (Google Sheets)
      n("8", "hitApi", "Update rank history in Google Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/RankHistory!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{searched_keyword}","{current_position}","{delta}","{direction}","{target_domain}","' +
                new Date().toISOString().split("T")[0] +
                '"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_appended",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**SEO Keyword Monitor** — Weekly rank tracking with AI diagnosis (8 steps)\n\n1. Runs every Monday at 6 AM\n2. Checks keyword rankings via **SerpAPI**\n3. **Code node** computes position deltas and detects drops\n4. **Significant drop?** (> 2 positions)\n   - Yes: AI diagnoses the cause and generates remediation steps → Slack alert\n   - No: skips diagnosis\n5. Weekly report emailed to SEO team (all keywords)\n6. Rank history appended to **Google Sheets** for trend tracking\n\n**To activate:** Say "connect openai", "connect serpapi", "connect google-sheets", "connect email", "connect slack"`,
  },
};
